import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  mailAccountConfigurationSchema,
  mailAccountInputSchema,
  mailAccountUpdateSchema,
  mailAccountListQuerySchema,
  mailAccountListResponseSchema,
  mailAccountLookupsSchema,
  type MailAccountInput,
  type MailAccountUpdate,
  type MailAccountListQuery,
} from '@erp/shared';
import { useSession } from '@/features/auth';
import { apiFetch } from '@/lib/api-client';
const base = '/api/v1/mail/accounts';
export function useMailScope() {
  const session = useSession();
  return {
    key: ['mail', session.data?.tenantId, session.data?.id] as const,
    enabled: Boolean(session.data),
  };
}
export function useMailAccounts(query: MailAccountListQuery) {
  const scope = useMailScope();
  return useQuery({
    queryKey: [...scope.key, 'accounts', query],
    enabled: scope.enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: () =>
      apiFetch(
        `${base}?${new URLSearchParams(
          Object.entries(mailAccountListQuerySchema.parse(query))
            .filter((entry): entry is [string, string | number] => entry[1] !== undefined)
            .map(([key, value]) => [key, String(value)]),
        ).toString()}`,
        mailAccountListResponseSchema,
      ),
  });
}
export function useMailAccount(id: string | null) {
  const scope = useMailScope();
  return useQuery({
    queryKey: [...scope.key, 'account', id],
    enabled: scope.enabled && id !== null,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: () => apiFetch(`${base}/${String(id)}`, mailAccountConfigurationSchema),
  });
}
export function useMailAccountLookups() {
  const scope = useMailScope();
  return useQuery({
    queryKey: [...scope.key, 'account-lookups'],
    enabled: scope.enabled,
    staleTime: 0,
    queryFn: () => apiFetch(`${base}/lookups`, mailAccountLookupsSchema),
  });
}
export function useCreateMailAccount() {
  const scope = useMailScope(),
    client = useQueryClient();
  return useMutation({
    mutationFn: ({ input, key }: { input: MailAccountInput; key: string }) =>
      apiFetch(base, mailAccountConfigurationSchema, {
        method: 'POST',
        body: mailAccountInputSchema.parse(input),
        headers: { 'Idempotency-Key': key },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: scope.key }),
  });
}
export function useUpdateMailAccount() {
  const scope = useMailScope(),
    client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: MailAccountUpdate }) =>
      apiFetch(`${base}/${id}`, mailAccountConfigurationSchema, {
        method: 'PUT',
        body: mailAccountUpdateSchema.parse(input),
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: scope.key }),
  });
}
